import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SectionHeader from '../components/SectionHeader';
import Pill from '../components/Pill';
import { fmt, ago } from '../utils/format';

// ── PO type badge helper ──────────────────────────────────────────────────────
const poTypeBadge = (ref) => {
  if (!ref) return { label: 'Manual', color: 'muted' };
  if (ref.startsWith('INV'))     return { label: 'PDF',  color: 'blue' };
  if (ref === 'Customer Orders') return { label: 'SM',   color: 'green' };
  return { label: 'Manual', color: 'muted' };
};

// ── Smart Buyer order lines (inline expand) ───────────────────────────────────
const SmartBuyerOrderLines = ({ orderId, onCloseableChange, hideDispatched }) => {
  const [soGroups, setSoGroups]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [ignoredIds, setIgnoredIds] = useState(new Set());
  const [actioning, setActioning]   = useState(null);
  const [notes, setNotes]           = useState({});
  const [savingNote, setSavingNote] = useState(null);

  useEffect(() => {
    const load = async () => {
      const { data: linesData } = await supabase
        .from('smart_buyer_order_lines').select('*').eq('smart_buyer_order_id', orderId);
      const lines = linesData || [];
      if (!lines.length) { setLoading(false); return; }

      const lineIds = lines.map(l => Number(l.id));
      const { data: linksData } = await supabase
        .from('smart_buyer_fulfilment_links').select('*').in('smart_buyer_order_line_id', lineIds);
      const links = linksData || [];

      const alreadyCancelled = new Set(links.filter(l => l.status === 'ignored').map(l => l.id));
      setIgnoredIds(alreadyCancelled);

      const initialNotes = {};
      for (const l of links) { if (l.notes) initialNotes[l.id] = l.notes; }
      setNotes(initialNotes);

      const soIds = [...new Set(links.map(l => l.sales_order_id).filter(Boolean))];
      if (!soIds.length) { setSoGroups([]); setLoading(false); return; }

      const { data: headersData } = await supabase
        .from('sales_order_header')
        .select('order_id, order_ref, member_id, first_name, last_name, company, billing_first_name, billing_last_name, stage, status, dispatched_date')
        .in('order_id', soIds);
      const headers = headersData || [];

      // sales_order_header.first_name/last_name/company are never populated
      // by the Cin7 sync (no memberFirstName/memberLastName/memberName on
      // that endpoint) -- the real customer name lives in the customers
      // table, keyed by member_id. No FK exists, so this is a manual lookup;
      // falls back to billing name for the ~3.6% of orders with no matching
      // customers row.
      const memberIds = [...new Set(headers.map(h => h.member_id).filter(Boolean))];
      const customerMap = {};
      if (memberIds.length) {
        const { data: customersData } = await supabase
          .from('customers')
          .select('member_id, first_name, last_name')
          .in('member_id', memberIds);
        for (const c of (customersData || [])) customerMap[c.member_id] = c;
      }

      const itemCodes = [...new Set(lines.map(l => l.item_code).filter(Boolean))];
      const { data: soItemsData } = await supabase
        .from('sales_order_items')
        .select('order_id, item_code, item_qty, item_qty_moved')
        .in('order_id', soIds).in('item_code', itemCodes);
      const soItems = soItemsData || [];

      const { data: receivedPoData } = await supabase
        .from('v_received_po_by_item')
        .select('item_code, po_number, fully_received_date')
        .in('item_code', itemCodes);
      const receivedPoByCode = {};
      for (const r of (receivedPoData || [])) receivedPoByCode[r.item_code] = r;

      const groups = headers.map(hdr0 => {
        const cust = customerMap[hdr0.member_id];
        const customer_name = (cust && [cust.first_name, cust.last_name].filter(Boolean).join(' '))
          || [hdr0.billing_first_name, hdr0.billing_last_name].filter(Boolean).join(' ')
          || hdr0.company
          || '—';
        const hdr = { ...hdr0, customer_name };
        const soLinks = links.filter(l => l.sales_order_id === hdr.order_id);
        const items = soLinks.map(link => {
          const orderLine  = lines.find(l => Number(l.id) === link.smart_buyer_order_line_id);
          const soItem     = soItems.find(i => i.order_id === hdr.order_id && i.item_code === link.item_code);
          const receivedPo = receivedPoByCode[link.item_code] || null;
          return {
            link_id:          link.id,
            link_status:      link.status || 'active',
            so_stage:         hdr.stage,
            so_status:        hdr.status,
            item_code:        link.item_code,
            item_name:        orderLine?.item_name || link.item_code,
            qty_ordered:      soItem?.item_qty ?? link.qty_to_fulfil,
            qty_received:     soItem?.item_qty_moved ?? 0,
            qty_moved:        soItem?.item_qty_moved ?? null,
            cost_nzd:         orderLine?.cost_nzd,
            po_number:        receivedPo?.po_number || null,
            po_received_date: receivedPo?.fully_received_date || null,
          };
        });
        return { header: hdr, items };
      });

      setSoGroups(groups);
      setLoading(false);

      const allLinks = groups.flatMap(g => g.items);
      const closeable = allLinks.length > 0 && allLinks.every(item =>
        item.link_status === 'ignored' || item.so_stage === 'Dispatched'
      );
      onCloseableChange && onCloseableChange(orderId, closeable);
    };
    load();
  }, [orderId]);

  const handleIgnore = async (e, linkId) => {
    e.stopPropagation();
    if (actioning) return;
    setActioning(linkId);
    const { error } = await supabase.from('smart_buyer_fulfilment_links').update({ status: 'ignored' }).eq('id', linkId);
    if (!error) {
      setIgnoredIds(prev => {
        const next = new Set([...prev, linkId]);
        const allItems = soGroups.flatMap(g => g.items);
        const closeable = allItems.length > 0 && allItems.every(item =>
          next.has(item.link_id) || item.so_stage === 'Dispatched'
        );
        onCloseableChange && onCloseableChange(orderId, closeable);
        return next;
      });
    } else { alert(`Failed to update: ${error.message}`); }
    setActioning(null);
  };

  const handleRestore = async (e, linkId) => {
    e.stopPropagation();
    if (actioning) return;
    setActioning(linkId);
    const { error } = await supabase.from('smart_buyer_fulfilment_links').update({ status: 'active' }).eq('id', linkId);
    if (!error) {
      setIgnoredIds(prev => {
        const next = new Set(prev);
        next.delete(linkId);
        const allItems = soGroups.flatMap(g => g.items);
        const closeable = allItems.length > 0 && allItems.every(item =>
          next.has(item.link_id) || item.so_stage === 'Dispatched'
        );
        onCloseableChange && onCloseableChange(orderId, closeable);
        return next;
      });
    } else { alert(`Failed to restore: ${error.message}`); }
    setActioning(null);
  };

  const handleNoteChange = (linkId, value) => setNotes(prev => ({ ...prev, [linkId]: value }));

  const handleNoteBlur = async (linkId) => {
    const note = notes[linkId] ?? '';
    setSavingNote(linkId);
    await supabase.from('smart_buyer_fulfilment_links').update({ notes: note || null }).eq('id', linkId);
    setSavingNote(null);
  };

  if (loading) return <div className="detail-loading">Loading...</div>;
  if (!soGroups.length) return <div className="detail-loading">No fulfilment data found</div>;

  const stageMap = {
    'Dispatched':                { color: 'green', label: 'Dispatched' },
    'Fully Picked - Hold':       { color: 'gold',  label: 'Picked - Hold' },
    'Problem Encountered':       { color: 'gold',  label: 'Problem' },
    'To Call Customer':          { color: 'gold',  label: 'Call Customer' },
    'Fraud Warning':             { color: 'red',   label: 'Fraud Warning' },
    'Awaiting Payment':          { color: 'gold',  label: 'Awaiting Payment' },
    'Payment Received':          { color: 'gold',  label: 'Payment Received' },
    'Ready to Collect':          { color: 'blue',  label: 'Ready to Collect' },
    'New':                       { color: 'blue',  label: 'New' },
    'Warehouse Transfer':        { color: 'blue',  label: 'Warehouse Transfer' },
    'HC - Back Order':           { color: 'muted', label: 'Back Order' },
    'Awaiting Traxxas Shipment': { color: 'muted', label: 'Awaiting Shipment' },
    'Awaiting Proxxon Shipment': { color: 'muted', label: 'Awaiting Shipment' },
    'Repair':                    { color: 'muted', label: 'Repair' },
  };

  const visibleGroups = hideDispatched
    ? soGroups.filter(({ header, items }) => {
        if (header.stage === 'Dispatched') return false;
        if (items.every(item => ignoredIds.has(item.link_id) || item.link_status === 'ignored')) return false;
        return true;
      })
    : soGroups;

  return (
    <div className="detail-wrap">
      <div className="detail-title">
        Sales Orders & Fulfilment
        {hideDispatched && visibleGroups.length === 0 && (
          <span style={{ marginLeft: 12, color: 'var(--green-light)', fontWeight: 400 }}>
            ✓ Nothing needs attention
          </span>
        )}
      </div>
      {visibleGroups.map(({ header, items }) => {
        const stageInfo    = stageMap[header.stage] || { color: 'muted', label: header.stage || 'Unknown' };
        const dispatchDate = header.stage === 'Dispatched' && header.dispatched_date
          ? ' ' + new Date(header.dispatched_date).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })
          : '';
        const isVoid   = header.status === 'VOID';
        const customer = header.customer_name;

        return (
          <div key={header.order_id} className="so-group">
            <div className="so-group__header">
              <span className="code-badge">{header.order_ref || header.order_id}</span>
              <span className="so-group__customer">{customer}</span>
              <span className={`pill pill--${stageInfo.color}`}>{stageInfo.label}{dispatchDate}</span>
              {isVoid && (
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--red, #ef4444)', marginLeft: 6 }}>
                  (VOID)
                </span>
              )}
            </div>
            <div className="so-group__items">
              {items.map((item, i) => {
                const isCancelled   = ignoredIds.has(item.link_id);
                const isBusy        = actioning === item.link_id;
                const fullyReceived = Number(item.qty_received) >= Number(item.qty_ordered);
                const poHint        = item.po_number
                  ? ` (Possibly: ${item.po_number}${item.po_received_date
                      ? ', ' + new Date(item.po_received_date).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })
                      : ''})`
                  : '';
                return (
                  <div key={i} className={`so-item ${isCancelled ? 'so-item--cancelled' : ''}`}>
                    <span className="code-badge code-badge--sm">{item.item_code}</span>
                    <span className="so-item__name">{item.item_name}</span>
                    <span className="so-item__qty">
                      ordered <strong>{fmt(item.qty_ordered)}</strong>
                      &nbsp;·&nbsp;
                      received <strong className={fullyReceived ? 'qty-ok' : 'qty-short'}>{fmt(item.qty_received)}</strong>
                      {item.qty_moved != null && (
                        <>&nbsp;·&nbsp;moved <strong className={Number(item.qty_moved) >= Number(item.qty_ordered) ? 'qty-ok' : 'qty-short'}>{fmt(item.qty_moved)}</strong></>
                      )}
                      {poHint && <span className="so-item__po-hint">{poHint}</span>}
                    </span>
                    {isCancelled ? (
                      <>
                        <span className="pill pill--green">Ignored</span>
                        <button className="ignore-btn ignore-btn--restore" onClick={(e) => handleRestore(e, item.link_id)} disabled={isBusy}>
                          {isBusy ? '…' : '↩ Restore'}
                        </button>
                      </>
                    ) : (
                      <>
                        <span className={`so-item__dot ${fullyReceived ? 'so-item__dot--green' : 'so-item__dot--red'}`}>●</span>
                        <button className="ignore-btn" onClick={(e) => handleIgnore(e, item.link_id)} disabled={isBusy} title="Mark as ignored">
                          {isBusy ? '…' : '✕ Ignore'}
                        </button>
                      </>
                    )}
                    <input
                      className={`note-input ${savingNote === item.link_id ? 'note-input--saving' : ''}`}
                      type="text"
                      placeholder="Add note…"
                      value={notes[item.link_id] || ''}
                      onChange={(e) => handleNoteChange(item.link_id, e.target.value)}
                      onBlur={() => handleNoteBlur(item.link_id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};


// ── Smart Buyer Orders table ──────────────────────────────────────────────────
const SmartBuyerOrders = ({ data, loading, showClosed, hideDispatched, onClose, onReset, expandId }) => {
  const [expanded, setExpanded]     = useState(null);
  const [actioning, setActioning]   = useState(null);
  const [closeableIds, setCloseableIds] = useState(new Set());

  // Search hands us a specific order to jump to — override whatever was
  // expanded before rather than requiring a second click.
  useEffect(() => {
    if (expandId != null) setExpanded(expandId);
  }, [expandId]);

  const handleCloseableChange = (orderId, isCloseable) => {
    setCloseableIds(prev => {
      const next = new Set(prev);
      if (isCloseable) next.add(orderId); else next.delete(orderId);
      return next;
    });
  };

  if (loading) return <div className="loading-row">Loading...</div>;
  if (!data.length) return <div className="empty-row">{showClosed ? 'No closed Smart Buyer orders' : 'No Smart Buyer orders placed yet'}</div>;

  const handleClose = async (e, id) => {
    e.stopPropagation();
    setActioning(id);
    await supabase.from('smart_buyer_orders').update({ is_closed: true }).eq('id', id);
    setActioning(null);
    onClose && onClose(id);
  };

  const handleReset = async (e, id) => {
    e.stopPropagation();
    setActioning(id);
    await supabase.from('smart_buyer_orders').update({ is_closed: false }).eq('id', id);
    setActioning(null);
    onReset && onReset(id);
  };

  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>Date</th><th>Supplier</th><th>Cin7 PO</th>
            <th className="num">Lines</th><th>Type</th><th>Status</th><th></th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <React.Fragment key={row.id}>
              <tr
                className={`data-row ${expanded === row.id ? 'data-row--expanded' : ''}`}
                onClick={() => setExpanded(expanded === row.id ? null : row.id)}
              >
                <td className="muted">{ago(row.created_at)}</td>
                <td><Pill label={row.supplier_name} color="blue" /></td>
                <td><span className="code-badge">{row.cin7_po_number || '—'}</span></td>
                <td className="num">{row.line_count}</td>
                <td><Pill label={row.run_type} color={row.run_type === 'auto' ? 'green' : 'gold'} /></td>
                <td><Pill label={row.status} color={row.status === 'submitted' ? 'green' : 'red'} /></td>
                <td onClick={(e) => e.stopPropagation()}>
                  {showClosed ? (
                    <button className="action-btn action-btn--reset" disabled={actioning === row.id} onClick={(e) => handleReset(e, row.id)}>
                      {actioning === row.id ? '…' : '↩ Reset'}
                    </button>
                  ) : (
                    <button
                      className="action-btn action-btn--close"
                      disabled={actioning === row.id || !closeableIds.has(row.id)}
                      onClick={(e) => handleClose(e, row.id, row.cin7_po_id)}
                      title={!closeableIds.has(row.id) ? 'Cannot close — expand row first, or some orders are not dispatched/ignored' : 'Close this Smart Buyer order'}
                    >
                      {actioning === row.id ? '…' : '✓ Close'}
                    </button>
                  )}
                </td>
              </tr>
              {expanded === row.id && (
                <tr className="detail-row">
                  <td colSpan={7}>
                    <SmartBuyerOrderLines orderId={row.id} onCloseableChange={handleCloseableChange} hideDispatched={hideDispatched} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Manual Fulfilment Linker ──────────────────────────────────────────────────
const ManualFulfilmentLinker = () => {
  const [orders, setOrders]               = useState([]);
  const [selectedId, setSelectedId]       = useState('');
  const [lines, setLines]                 = useState([]);
  const [loadingLines, setLoadingLines]   = useState(false);
  const [soInputs, setSoInputs]           = useState({});
  const [lookups, setLookups]             = useState({});
  const [looking, setLooking]             = useState(null);
  const [linking, setLinking]             = useState(null);
  const [existingLinks, setExistingLinks] = useState({});

  useEffect(() => {
    supabase.from('smart_buyer_orders').select('id, cin7_po_number, run_date, supplier_name')
      .eq('is_closed', false).order('created_at', { ascending: false }).limit(100)
      .then(({ data }) => setOrders(data || []));
  }, []);

  const loadLines = async (orderId) => {
    setSelectedId(orderId); setLines([]); setSoInputs({}); setLookups({}); setExistingLinks({});
    if (!orderId) return;
    setLoadingLines(true);
    const { data: linesData } = await supabase.from('smart_buyer_order_lines').select('*').eq('smart_buyer_order_id', orderId);
    const ls = linesData || [];
    setLines(ls);
    if (ls.length > 0) {
      const lineIds = ls.map(l => Number(l.id));
      const { data: linksData } = await supabase.from('smart_buyer_fulfilment_links').select('*').in('smart_buyer_order_line_id', lineIds);
      const allLinks = linksData || [];
      const soIds = [...new Set(allLinks.map(l => l.sales_order_id).filter(Boolean))];
      let headerMap = {};
      if (soIds.length > 0) {
        const { data: hdrs } = await supabase.from('sales_order_header').select('order_id, order_ref, company, first_name, last_name').in('order_id', soIds);
        for (const h of (hdrs || [])) headerMap[h.order_id] = h;
      }
      const byLine = {};
      for (const link of allLinks) {
        const key = Number(link.smart_buyer_order_line_id);
        byLine[key] = byLine[key] || [];
        byLine[key].push({ ...link, soHeader: headerMap[link.sales_order_id] || null });
      }
      setExistingLinks(byLine);
    }
    setLoadingLines(false);
  };

  const handleLookup = async (line) => {
    const ref = (soInputs[line.id] || '').trim().toUpperCase();
    if (!ref) return;
    setLooking(line.id);
    const { data: headers } = await supabase.from('sales_order_header').select('order_id, order_ref, company, first_name, last_name').eq('order_ref', ref).limit(1);
    if (!headers || !headers.length) { setLookups(prev => ({ ...prev, [line.id]: { error: `SO ${ref} not found` } })); setLooking(null); return; }
    const hdr = headers[0];
    const { data: soItems } = await supabase.from('sales_order_items').select('item_qty, item_qty_moved').eq('order_id', hdr.order_id).eq('item_code', line.item_code).limit(1);
    let qtyOutstanding = line.qty_ordered;
    if (soItems && soItems.length) { const si = soItems[0]; qtyOutstanding = Math.max(0, (si.item_qty || 0) - (si.item_qty_moved || 0)); }
    const customer = hdr.company || [hdr.first_name, hdr.last_name].filter(Boolean).join(' ') || '—';
    setLookups(prev => ({ ...prev, [line.id]: { ref: hdr.order_ref, order_id: hdr.order_id, customer, qty: qtyOutstanding, error: null } }));
    setLooking(null);
  };

  const handleLink = async (line) => {
    const lookup = lookups[line.id];
    if (!lookup || lookup.error) return;
    setLinking(line.id);
    const { error } = await supabase.from('smart_buyer_fulfilment_links').insert({
      smart_buyer_order_line_id: line.id, sales_order_id: lookup.order_id,
      sales_order_ref: lookup.ref, item_code: line.item_code, qty_to_fulfil: lookup.qty, status: 'active',
    });
    if (!error) {
      const { data: linksData } = await supabase.from('smart_buyer_fulfilment_links').select('*').eq('smart_buyer_order_line_id', line.id);
      const freshLinks = linksData || [];
      const freshSoIds = [...new Set(freshLinks.map(l => l.sales_order_id).filter(Boolean))];
      let freshHeaderMap = {};
      if (freshSoIds.length > 0) {
        const { data: hdrs } = await supabase.from('sales_order_header').select('order_id, order_ref, company, first_name, last_name').in('order_id', freshSoIds);
        for (const h of (hdrs || [])) freshHeaderMap[h.order_id] = h;
      }
      const enrichedLinks = freshLinks.map(l => ({ ...l, soHeader: freshHeaderMap[l.sales_order_id] || null }));
      setExistingLinks(prev => ({ ...prev, [Number(line.id)]: enrichedLinks }));
      setSoInputs(prev => ({ ...prev, [line.id]: '' }));
      setLookups(prev => { const n = { ...prev }; delete n[line.id]; return n; });
    } else { alert(`Failed to link: ${error.message}`); }
    setLinking(null);
  };

  return (
    <section className="section">
      <SectionHeader title="Manual Fulfilment Links" count={null} onRefresh={() => loadLines(selectedId)} loading={loadingLines} />
      <p className="section__desc">Manually associate Sales Orders with Smart Buyer order lines.</p>
      <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)' }}>
        <select value={selectedId} onChange={e => loadLines(e.target.value)}
          style={{ background: 'var(--bg3)', color: 'var(--white)', border: '1px solid var(--border2)', borderRadius: 4, padding: '6px 12px', fontFamily: 'var(--font-body)', fontSize: 13, cursor: 'pointer', minWidth: 320 }}>
          <option value="">— Select a Smart Buyer PO —</option>
          {orders.map(o => <option key={o.id} value={o.id}>{o.cin7_po_number} — {o.supplier_name} — {o.run_date}</option>)}
        </select>
      </div>
      {loadingLines && <div className="loading-row">Loading lines...</div>}
      {!loadingLines && selectedId && !lines.length && <div className="empty-row">No lines found for this order</div>}
      {!loadingLines && lines.length > 0 && (
        <div style={{ padding: '16px 24px' }}>
          {lines.map(line => {
            const lookup = lookups[line.id];
            const existing = existingLinks[Number(line.id)] || [];
            const isLinked = existing.length > 0;
            return (
              <div key={line.id} className="order-line">
                <div className="order-line__header" style={{ background: isLinked ? 'rgba(26,107,60,0.15)' : undefined, borderBottom: isLinked ? '1px solid rgba(34,197,94,0.3)' : undefined }}>
                  <span className="code-badge">{line.item_code}</span>
                  <span className="order-line__name">{line.item_name}</span>
                  <span className="order-line__qty">qty <strong>{line.qty_ordered}</strong></span>
                  {line.cost_nzd && <span className="order-line__cost">${Number(line.cost_nzd).toFixed(2)}</span>}
                  {isLinked && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 8 }}>
                      <span style={{ fontSize: 10, color: 'var(--green-light)', fontWeight: 700 }}>✓ Linked</span>
                      {existing.map(lk => <span key={lk.id} className="code-badge code-badge--sm" style={{ color: 'var(--green-light)', borderColor: 'rgba(34,197,94,0.3)' }}>{lk.sales_order_ref}</span>)}
                    </span>
                  )}
                </div>
                {existing.length > 0 && (
                  <div className="order-line__links">
                    {existing.map(lk => {
                      const hdr = lk.soHeader;
                      const cust = hdr ? (hdr.company || [hdr.first_name, hdr.last_name].filter(Boolean).join(' ') || '—') : '—';
                      return (
                        <div key={lk.id} className="fulfil-link">
                          <span className="fulfil-link__ref">{lk.sales_order_ref}</span>
                          <span className="fulfil-link__qty">{cust} · qty {lk.qty_to_fulfil}</span>
                          <span style={{ fontSize: 10, color: 'var(--muted2)' }}>{lk.status}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, borderTop: existing.length ? '1px solid var(--border)' : 'none' }}>
                  <input type="text" placeholder="SO ref e.g. HC94572" value={soInputs[line.id] || ''}
                    onChange={e => setSoInputs(prev => ({ ...prev, [line.id]: e.target.value.toUpperCase() }))}
                    onKeyDown={e => e.key === 'Enter' && handleLookup(line)}
                    style={{ background: 'var(--bg)', border: '1px solid var(--border2)', borderRadius: 3, color: 'var(--white)', fontFamily: 'var(--font-mono)', fontSize: 12, padding: '4px 10px', width: 160, outline: 'none' }} />
                  <button className="action-btn action-btn--ghost" onClick={() => handleLookup(line)} disabled={looking === line.id || !soInputs[line.id]}>
                    {looking === line.id ? '…' : 'Lookup'}
                  </button>
                  {lookup && !lookup.error && (
                    <>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--code)' }}>{lookup.ref}</span>
                      <span style={{ fontSize: 12, color: 'var(--white)' }}>{lookup.customer}</span>
                      <span style={{ fontSize: 12, color: 'var(--muted)' }}>qty outstanding: <strong style={{ color: 'var(--white)' }}>{lookup.qty}</strong></span>
                      <button className="action-btn action-btn--close" onClick={() => handleLink(line)} disabled={linking === line.id}>
                        {linking === line.id ? '…' : '+ Link'}
                      </button>
                    </>
                  )}
                  {lookup && lookup.error && <span style={{ fontSize: 12, color: 'var(--red-light)' }}>{lookup.error}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

// ── Duplicate PO Finder ───────────────────────────────────────────────────────
const DuplicatePOFinder = () => {
  const [running, setRunning]   = useState(false);
  const [matches, setMatches]   = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [voiding, setVoiding]   = useState(null);
  const [ran, setRan]           = useState(false);

  const runAnalysis = async () => {
    setRunning(true); setMatches([]); setRan(false);
    const { data: receivedData } = await supabase.from('purchase_order_header')
      .select('order_id, order_ref, supplier_invoice_ref, stage, status, created_date, total')
      .eq('member_id', 5226).eq('stage', 'Received').neq('status', 'VOID')
      .order('created_date', { ascending: false }).limit(10);
    const receivedPos = receivedData || [];
    const { data: openData } = await supabase.from('purchase_order_header')
      .select('order_id, order_ref, supplier_invoice_ref, stage, status, created_date, total')
      .eq('member_id', 5226).neq('status', 'VOID').order('created_date', { ascending: false });
    const openPos = (openData || []).filter(h => !h.stage || h.stage !== 'Received');

    if (!receivedPos.length || !openPos.length) { setRunning(false); setRan(true); return; }

    const allIds = [...new Set([...receivedPos.map(p => p.order_id), ...openPos.map(p => p.order_id)])];
    const { data: items } = await supabase.from('purchase_order_items').select('order_id, item_code, item_qty').in('order_id', allIds);
    const itemsByPo = {};
    for (const item of (items || [])) { if (!itemsByPo[item.order_id]) itemsByPo[item.order_id] = []; itemsByPo[item.order_id].push(item); }
    const buildSet = (po) => new Map((itemsByPo[po.order_id] || []).map(l => [l.item_code, l.item_qty]));

    const found = [];
    for (const received of receivedPos) {
      const setA = buildSet(received);
      if (!setA.size) continue;
      for (const open of openPos) {
        const setB = buildSet(open);
        if (!setB.size) continue;
        const shared = [], sharedCodeOnly = [];
        for (const [code, qty] of setA) {
          if (setB.has(code)) {
            if (setB.get(code) === qty) shared.push({ code, qty });
            else sharedCodeOnly.push({ code, qtyA: qty, qtyB: setB.get(code) });
          }
        }
        const totalMatched = shared.length + sharedCodeOnly.length;
        if (!totalMatched) continue;
        const matchPctA = Math.round((totalMatched / setA.size) * 100);
        const matchPctB = Math.round((totalMatched / setB.size) * 100);
        const matchPct  = Math.max(matchPctA, matchPctB);
        if (matchPct >= 60) found.push({ a: received, b: open, matchPct, matchPctA, matchPctB, shared, sharedCodeOnly, onlyInA: [...setA.keys()].filter(c => !setB.has(c)), onlyInB: [...setB.keys()].filter(c => !setA.has(c)) });
      }
    }
    found.sort((a, b) => new Date(b.a.created_date) - new Date(a.a.created_date) || b.matchPct - a.matchPct);
    setMatches(found); setRunning(false); setRan(true);
  };

  const handleVoid = async (e, po) => {
    e.stopPropagation();
    if (!window.confirm(`Void ${po.order_ref}? This cannot be undone.`)) return;
    setVoiding(po.order_id);
    alert(`To void ${po.order_ref}, please void it directly in Cin7.\n\nThis matcher has identified it as a likely duplicate.`);
    setVoiding(null);
  };

  const matchColor = (pct) => pct >= 80 ? 'green' : 'gold';

  return (
    <section className="section">
      <SectionHeader title="Duplicate PO Finder" count={ran ? matches.length : null} onRefresh={runAnalysis} loading={running}
        actions={<button className="action-btn action-btn--ghost" onClick={runAnalysis} disabled={running}>{running ? '…' : '▶ Run Analysis'}</button>} />
      <p className="section__desc">Compares the 10 most recent Received Bringans POs against all open POs. Flags open POs with overlapping items that may be duplicates.</p>
      {!ran && !running && <div className="loading-row">Click "Run Analysis" to find duplicate POs</div>}
      {running && <div className="loading-row">Analysing open POs…</div>}
      {ran && !matches.length && <div className="empty-row">✓ No duplicate POs found</div>}
      {matches.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Received PO</th><th>Open PO</th><th className="num">Match A / B</th>
                <th className="num">Shared</th><th>Received Ref</th><th>Open Ref</th>
                <th>Received Date</th><th>Open Date</th><th></th>
              </tr>
            </thead>
            <tbody>
              {matches.map((m) => {
                const key = `${m.a.order_id}-${m.b.order_id}`;
                const isExp = expanded === key;
                const color = matchColor(m.matchPct);
                return (
                  <React.Fragment key={key}>
                    <tr className={`data-row ${isExp ? 'data-row--expanded' : ''}`} onClick={() => setExpanded(isExp ? null : key)}>
                      <td><span className="code-badge">{m.a.order_ref}</span>{' '}<span className={`pill pill--${poTypeBadge(m.a.supplier_invoice_ref).color}`} style={{ fontSize: 9 }}>{poTypeBadge(m.a.supplier_invoice_ref).label}</span></td>
                      <td><span className="code-badge">{m.b.order_ref}</span>{' '}<span className={`pill pill--${poTypeBadge(m.b.supplier_invoice_ref).color}`} style={{ fontSize: 9 }}>{poTypeBadge(m.b.supplier_invoice_ref).label}</span></td>
                      <td className="num"><span className={`pill pill--${color}`}>{m.matchPctA}% / {m.matchPctB}%</span></td>
                      <td className="num">{m.shared.length + m.sharedCodeOnly.length}</td>
                      <td className="muted">{m.a.supplier_invoice_ref || '—'}</td>
                      <td className="muted">{m.b.supplier_invoice_ref || '—'}</td>
                      <td className="muted">{m.a.created_date?.slice(0, 10)}</td>
                      <td className="muted">{m.b.created_date?.slice(0, 10)}</td>
                      <td onClick={e => e.stopPropagation()}>
                        <button className="action-btn action-btn--close" style={{ fontSize: 10 }} disabled={voiding === m.b.order_id} onClick={e => handleVoid(e, m.b)} title={`Void open PO: ${m.b.order_ref}`}>
                          Void {m.b.order_ref}
                        </button>
                      </td>
                    </tr>
                    {isExp && (
                      <tr className="detail-row">
                        <td colSpan={9}>
                          <div className="detail-wrap">
                            <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
                              {m.shared.length > 0 && (
                                <div>
                                  <div className="detail-title" style={{ color: 'var(--green-light)' }}>✓ Exact Matches ({m.shared.length})</div>
                                  {m.shared.map(s => <div key={s.code} style={{ fontSize: 12, marginBottom: 4 }}><span className="code-badge code-badge--sm">{s.code}</span><span style={{ marginLeft: 8, color: 'var(--muted)' }}>qty {s.qty}</span></div>)}
                                </div>
                              )}
                              {m.sharedCodeOnly.length > 0 && (
                                <div>
                                  <div className="detail-title" style={{ color: 'var(--gold)' }}>~ Diff Qty ({m.sharedCodeOnly.length})</div>
                                  {m.sharedCodeOnly.map(s => <div key={s.code} style={{ fontSize: 12, marginBottom: 4 }}><span className="code-badge code-badge--sm">{s.code}</span><span style={{ marginLeft: 8, color: 'var(--muted)' }}>A:{s.qtyA} / B:{s.qtyB}</span></div>)}
                                </div>
                              )}
                              {m.onlyInA.length > 0 && (
                                <div>
                                  <div className="detail-title" style={{ color: 'var(--muted)' }}>Only in {m.a.order_ref} ({m.onlyInA.length})</div>
                                  {m.onlyInA.map(c => <div key={c} style={{ fontSize: 12, marginBottom: 4 }}><span className="code-badge code-badge--sm">{c}</span></div>)}
                                </div>
                              )}
                              {m.onlyInB.length > 0 && (
                                <div>
                                  <div className="detail-title" style={{ color: 'var(--muted)' }}>Only in {m.b.order_ref} ({m.onlyInB.length})</div>
                                  {m.onlyInB.map(c => <div key={c} style={{ fontSize: 12, marginBottom: 4 }}><span className="code-badge code-badge--sm">{c}</span></div>)}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

// ── SmartBuyer Page ───────────────────────────────────────────────────────────
const SmartBuyer = () => {
  const navigate = useNavigate();
  const [smartBuyerOrders, setSmartBuyerOrders]     = useState([]);
  const [loadingSBO, setLoadingSBO]                 = useState(true);
  const [showClosed, setShowClosed]                 = useState(false);
  const [hideDispatched, setHideDispatched]         = useState(false);
  const [lastRefresh, setLastRefresh]               = useState(new Date());
  const [searchInput, setSearchInput]               = useState('');
  const [searching, setSearching]                   = useState(false);
  const [searchResults, setSearchResults]           = useState(null); // null = no active search
  const [searchMessage, setSearchMessage]           = useState('');

  const loadSmartBuyerOrders = useCallback(async (closed = false) => {
    setLoadingSBO(true);
    const { data } = await supabase.from('smart_buyer_orders').select('*').eq('is_closed', closed).order('created_at', { ascending: false }).limit(50);
    setSmartBuyerOrders(data || []);
    setLoadingSBO(false);
  }, []);

  useEffect(() => { loadSmartBuyerOrders(); }, [loadSmartBuyerOrders]);

  const refreshAll = () => { loadSmartBuyerOrders(showClosed); setLastRefresh(new Date()); };

  // Finds a Smart Buyer order either directly (PO ref) or indirectly (sales
  // order ref, via its fulfilment link) -- searches across open AND closed
  // orders regardless of the current showClosed toggle, since a lookup is a
  // targeted "find this one" action, not a filter of the current view.
  const handleSearch = async () => {
    const term = searchInput.trim().toUpperCase();
    if (!term) { setSearchResults(null); setSearchMessage(''); return; }

    setSearching(true);
    setSearchMessage('');

    const { data: byPo } = await supabase
      .from('smart_buyer_orders')
      .select('*')
      .ilike('cin7_po_number', `%${term}%`)
      .limit(20);

    const { data: links } = await supabase
      .from('smart_buyer_fulfilment_links')
      .select('smart_buyer_order_line_id')
      .ilike('sales_order_ref', `%${term}%`);

    let byLinkedOrders = [];
    if (links && links.length) {
      const lineIds = [...new Set(links.map(l => l.smart_buyer_order_line_id))];
      const { data: soLines } = await supabase
        .from('smart_buyer_order_lines')
        .select('smart_buyer_order_id')
        .in('id', lineIds);
      const orderIds = [...new Set((soLines || []).map(l => l.smart_buyer_order_id))];
      if (orderIds.length) {
        const { data: linkedOrders } = await supabase
          .from('smart_buyer_orders')
          .select('*')
          .in('id', orderIds);
        byLinkedOrders = linkedOrders || [];
      }
    }

    const merged = [...(byPo || []), ...byLinkedOrders]
      .filter((o, i, arr) => arr.findIndex(x => x.id === o.id) === i)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    if (merged.length) {
      setSearchResults(merged);
      setSearching(false);
      return;
    }

    // Nothing tied to a Smart Buyer order -- check whether the ref exists at
    // all, so "not found" doesn't just mean "not on this page".
    const [{ data: soHit }, { data: poHit }] = await Promise.all([
      supabase.from('sales_order_header').select('order_ref').ilike('order_ref', `%${term}%`).limit(1),
      supabase.from('purchase_order_header').select('order_ref').ilike('order_ref', `%${term}%`).limit(1),
    ]);
    if (soHit && soHit.length) {
      setSearchMessage(`Sales order ${soHit[0].order_ref} exists but isn't linked to any Smart Buyer order.`);
    } else if (poHit && poHit.length) {
      setSearchMessage(`PO ${poHit[0].order_ref} exists but wasn't created by Smart Buyer.`);
    } else {
      setSearchMessage(`No sales order or PO found matching "${term}".`);
    }
    setSearchResults([]);
    setSearching(false);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchResults(null);
    setSearchMessage('');
  };

  const handleToggleClosed = () => { const next = !showClosed; setShowClosed(next); loadSmartBuyerOrders(next); };
  const handleOrderClosed  = (id) => setSmartBuyerOrders(prev => prev.filter(r => r.id !== id));
  const handleOrderReset   = (id) => setSmartBuyerOrders(prev => prev.filter(r => r.id !== id));

  return (
    <main className="app-main">
      <div className="page-nav">
        <button className="back-btn" onClick={() => navigate('/dispatch-tools')}>← Dispatch Tools</button>
        <span className="page-nav__title">Smart Buyer</span>
        <input
          type="text"
          placeholder="Find SO or PO ref…"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
          style={{
            background: 'var(--bg3)', color: 'var(--white)',
            border: '1px solid var(--border2)', borderRadius: 4,
            padding: '5px 10px', fontFamily: 'var(--font-mono)',
            fontSize: 12, width: 160, outline: 'none', marginLeft: 12,
          }}
        />
        <button className="app-header__btn" onClick={handleSearch} disabled={searching}>
          {searching ? '…' : '🔎 Find'}
        </button>
        {searchResults !== null && (
          <button className="app-header__btn" onClick={handleClearSearch}>✕ Clear</button>
        )}
        <button className="back-btn" onClick={() => navigate('/customer-orders-no-stock')}>📊 No Stock Orders</button>
        <span className="app-header__refresh">Last refresh: {lastRefresh.toLocaleTimeString('en-NZ')}</span>
        <button className="app-header__btn" onClick={refreshAll}>↻ Refresh All</button>
      </div>

      <section className="section">
        <SectionHeader
          title={searchResults !== null ? `Search Results — "${searchInput.trim()}"` : 'Smart Buyer Orders Placed'}
          count={searchResults !== null ? searchResults.length : smartBuyerOrders.length}
          onRefresh={() => loadSmartBuyerOrders(showClosed)}
          loading={loadingSBO || searching}
          actions={
            searchResults !== null ? (
              <button className="action-btn action-btn--ghost" onClick={handleClearSearch}>✕ Clear Search</button>
            ) : (
              <>
                <button className={`action-btn ${hideDispatched ? 'action-btn--active' : 'action-btn--ghost'}`} onClick={() => setHideDispatched(h => !h)}>
                  {hideDispatched ? '⊙ Show Dispatched' : '⊘ Hide Dispatched'}
                </button>
                <button className={`action-btn ${showClosed ? 'action-btn--active' : 'action-btn--ghost'}`} onClick={handleToggleClosed}>
                  {showClosed ? '← Active Orders' : '⊘ Show Closed'}
                </button>
              </>
            )
          }
        />
        <p className="section__desc">
          {searchResults !== null
            ? (searchMessage || 'Matching Smart Buyer order(s) — includes closed orders, regardless of the toggle above.')
            : (showClosed ? 'Closed Smart Buyer orders — all lines dispatched or manually managed.' : 'Purchase orders created by Smart Buyer, with fulfilment links to customer orders.')}
        </p>
        <SmartBuyerOrders
          data={searchResults !== null ? searchResults : smartBuyerOrders}
          loading={loadingSBO || searching}
          showClosed={showClosed}
          hideDispatched={hideDispatched}
          onClose={handleOrderClosed}
          onReset={handleOrderReset}
          expandId={searchResults && searchResults.length === 1 ? searchResults[0].id : null}
        />
      </section>

      <ManualFulfilmentLinker />
      <DuplicatePOFinder />
    </main>
  );
};

export default SmartBuyer;
